/**
 * This file tracks events specific to profile pages.
 * In this context, profile page means the entity page
 * for a user or team displaying its projects, reports, etc.
 */
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {ShareProfileType} from '../../pages/EntityPage';
import {makeTrackEvent} from './makeTrackEvent';

type ProfilePageEventData = {
  entityName: string;
};

type ProfilePageEventDataAnnotation = {
  entityName: {
    description: `The name of the entity`;
    exampleValues: [`wandb`];
  };
};

export const entityReportCreated = makeTrackEvent<
  ProfilePageEventData,
  ProfilePageEventDataAnnotation & {
    _description: `An intro report for a profile page was created`;
    _location: `Profile page`;
    _motivation: `How many entity reports have been created?`;
  }
>(`Entity report created`);

type ShareProfileEventData = ProfilePageEventData & {
  shareType: ShareProfileType;
};

type ShareProfileEventDataAnnotation = ProfilePageEventDataAnnotation & {
  shareType: {
    description: `The type of sharing (copy URL, social media platforms, etc.)`;
    exampleValues: [ShareProfileType.copy];
  };
};

export const profileShared = makeTrackEvent<
  ShareProfileEventData,
  ShareProfileEventDataAnnotation & {
    _description: `A user shared a profile page with our share functionality`;
    _location: `Profile page`;
    _motivation: `How many times have profile pages been shared?`;
  }
>(`Profile shared`);

export const profileShareViewed = makeTrackEvent<
  ShareProfileEventData,
  ShareProfileEventDataAnnotation & {
    _description: `A profile page shared by our share functionality was viewed`;
    _location: `Profile page`;
    _motivation: `What is the conversion rate of profile sharing?`;
  }
>(`Profile share viewed`);

export const profileLinkAdded = makeTrackEvent<
  ProfilePageEventData & {linkName: string; linkURL: string},
  ProfilePageEventDataAnnotation & {
    linkName: {
      description: `The user-defined name for the link`;
      exampleValues: [`My NFT project`];
    };
    linkURL: {
      description: `The link URL`;
      exampleValues: [`https://wandb.ai`];
    };
    _description: `A link was added to a profile page`;
    _location: `Profile page`;
    _motivation: `What are the usage patterns for profile page links?`;
  }
>(`Profile link added`);

export const profileReportShowcased = makeTrackEvent<
  ProfilePageEventData & {reportID: string},
  ProfilePageEventDataAnnotation & {
    reportID: {
      description: `The base64-encoded ID of the showcased report`;
      exampleValues: [];
    };
    _description: `A report was showcased on a profile page`;
    _location: `Profile page`;
    _motivation: `What are the usage patterns for profile page report showcase?`;
  }
>(`Profile report showcased`);

export const profileProjectShowcased = makeTrackEvent<
  ProfilePageEventData & {projectID: string},
  ProfilePageEventDataAnnotation & {
    projectID: {
      description: `The base64-encoded ID of the showcased project`;
      exampleValues: [];
    };
    _description: `A project was showcased on a profile page`;
    _location: `Profile page`;
    _motivation: `What are the usage patterns for profile page project showcase?`;
  }
>(`Profile project showcased`);
